// TierBadge.jsx
import React, { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Zap, Sprout, Rocket, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const TierIcon = ({ tier }) => {
  const iconProps = { size: 20, className: "flex-shrink-0" };
  switch(tier) {
    case 'starter': return <Sprout {...iconProps} />;
    case 'business': return <Rocket {...iconProps} />;
    case 'enterprise': return <Star {...iconProps} />;
    default: return <Sprout {...iconProps} />;
  }
};

function TierBadgeComponent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/subscription/details`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          setSubscription(data.subscription);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubscriptionDetails();
  }, []);

  const handleUpgradeClick = () => {
    navigate('/pricing');
  };

  if (loading) {
    return (
      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-4 py-2 animate-pulse">
        <div className="h-4 w-32 bg-gray-300 dark:bg-gray-700 rounded"></div>
      </div>
    );
  }

  if (!subscription) return null;

  const tierColors = {
    starter: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    business: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    enterprise: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800'
  };

  const tierColor = tierColors[subscription.tier] || tierColors.starter;
  const isUnlimited = subscription.tier === 'enterprise';
  const usagePercentage = subscription.screenUsagePercentage || 0;
  const isNearLimit = usagePercentage >= 80 && !isUnlimited;

  return (
    <div className={`border rounded-lg px-4 py-3 ${tierColor} transition-all duration-200 hover:shadow-md`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`
            p-2 rounded-lg
            ${subscription.tier === 'starter' ? 'bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-200' : ''}
            ${subscription.tier === 'business' ? 'bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-200' : ''}
            ${subscription.tier === 'enterprise' ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-200' : ''}
          `}>
            <TierIcon tier={subscription.tier} />
          </div>
          <div>
            <div className="font-semibold text-sm">
              {t(`pricing.tiers.${subscription.tier}`)}
              {subscription.tier === 'business' && (
                <span className="ml-2 text-xs opacity-75">
                  ({t('pricing.popular')})
                </span>
              )}
            </div>
            <div className="text-xs opacity-75">
              {isUnlimited ? (
                t('pricing.usage.unlimitedUsage', { current: subscription.screenCount })
              ) : (
                t('pricing.usage.currentUsage', { 
                  current: subscription.screenCount, 
                  limit: subscription.screenLimit 
                })
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!isUnlimited && (
            <div className="hidden sm:block w-24">
              <div className="w-full bg-gray-300 dark:bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    isNearLimit 
                      ? 'bg-red-500' 
                      : subscription.tier === 'starter' 
                        ? 'bg-blue-500' 
                        : 'bg-purple-500'
                  }`}
                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                ></div>
              </div>
              <div className="text-xs text-center mt-1 opacity-75">
                {usagePercentage}%
              </div>
            </div>
          )}

          {subscription.tier !== 'enterprise' && (
            <button
              onClick={handleUpgradeClick}
              className={`
                px-3 py-1.5 rounded-md text-xs font-medium
                transition-all duration-200
                flex items-center gap-1.5
                ${isNearLimit 
                  ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse' 
                  : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                }
              `}
            >
              {isNearLimit ? (
                <>
                  <Zap size={14} />
                  {t('pricing.limits.upgradeRequired')}
                </>
              ) : (
                <>
                  <TrendingUp size={14} />
                  {t('pricing.actions.upgradePlan')}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {isNearLimit && (
        <div className="mt-2 pt-2 border-t border-red-300 dark:border-red-700">
          <p className="text-xs text-red-700 dark:text-red-300">
            ⚠️ {t('pricing.limits.screenLimitMessage', {
              tier: t(`pricing.tiers.${subscription.tier}`),
              current: subscription.screenCount,
              limit: subscription.screenLimit
            })}
          </p>
        </div>
      )}
    </div>
  );
}

// ✅ Mit memo wrappen - VERHINDERT unnötige Re-Renders!
const TierBadge = memo(TierBadgeComponent);

export default TierBadge;